import ee from '../../services/earth-engine';
import { eeEval, getSelectedSatellite } from '../utils/index.js';
import { observedDepthImage } from './depthImage';
import { presetContextOf } from './preset';
import { classifiersFromModel, missingPredictors, modelMismatch } from './modelStore';
import { errorByDepthBin, splitMasks } from './validation';
import {
  applyTreeModel,
  classifierFor,
  kindsFor,
  predictorImage,
  predictorNames,
  trainTreeModel,
  treeMetricsReport,
  treeParamsOf,
} from './trees';
import {
  crossValidate,
  CV_SCHEMES,
  ensembleWeightFromOutOfFold,
  FOLD_PROPERTY,
  metricsOf,
  populatedFolds,
  stratifiedTrainingSamples,
  withPredictedDepth,
  withResiduals,
} from './crossValidation';

/**
 * @function abort
 * @description A refusal the page can report. `persist` marks the ones that leave the screen
 * empty, which stay on as an alert rather than passing by as a snackbar.
 * @param {String} message - The wording, already translated.
 * @param {Boolean} [persist] - Whether it should stay on screen.
 * @memberof module:Bathymetry
 * @returns {Object} `{error: {message, persist}}`.
 */
const abort = (message, persist = true) => ({ error: { message, persist } });

/**
 * @function applyLoadedModel
 * @description Applies a model saved from an earlier run to this imagery, without refitting.
 * Refused when the file was trained on different imagery, or when it is missing a predictor this
 * run would feed it: either would score a model against inputs it never saw.
 * @param {Object} params
 * @param {Object} params.loaded - The model file.
 * @param {Object} params.data - The analysis configuration.
 * @param {String} params.type - The imagery's processing level, TOA or SR.
 * @param {Object} params.context - The predictor context the run would build.
 * @param {Array<String>} params.predictors - The predictors that context yields.
 * @param {Object} params.params - The tree parameters this run is set to.
 * @param {Function} params.t - The translator.
 * @memberof module:Bathymetry
 * @returns {Object} `{image, coefficients}`, or an abort.
 */
const applyLoadedModel = ({ loaded, data, type, context, predictors, params, t }) => {
  const mismatch = modelMismatch(loaded, presetContextOf(getSelectedSatellite(), type, data.bands));
  if (mismatch) return abort(t('forms.acquisition.4.bathymetry.modelStore.mismatch', mismatch));

  const absent = missingPredictors(loaded, predictors);
  if (absent.length) {
    return abort(t('forms.acquisition.4.bathymetry.modelStore.missingPredictors', { names: absent.join(', ') }));
  }

  const calibration = { ...params, ensembleWeightRf: loaded.ensembleWeightRf ?? params.ensembleWeightRf };
  return {
    image: applyTreeModel(data.image, { ...context, params: calibration, classifiers: classifiersFromModel(loaded) }),
    coefficients: {
      ...treeMetricsReport(data.modelSelected, loaded.predictors, calibration),
      MODEL_SOURCE: 'LOADED',
      MODEL_TRAINED_ON: loaded.trainedOn?.imageDate ?? '—',
      MODEL_TRAINED_SAMPLES: loaded.trainedOn?.samples ?? null,
    },
  };
};

/**
 * @function crossValidateSamples
 * @description Scores the model out of fold, and with it weights the two members of an ensemble.
 * Folds that hold no sample are dropped first: a block scheme over an area smaller than one block
 * puts every sounding in the same fold, and holding that fold out leaves nothing to train on.
 * Fewer than two populated folds means the scheme cannot run at all, which is reported rather
 * than silently skipped - the user asked for validation and would otherwise read the in-sample
 * error as if it were validated.
 * @param {Object} samples - The training samples, carrying their fold.
 * @param {Object} params
 * @param {Array<String>} params.kinds - The classifier kinds to score.
 * @param {Object} params.treeParams - The tree parameters.
 * @param {Function} params.train - How one fold is trained.
 * @param {Function} params.t - The translator.
 * @memberof module:Bathymetry
 * @returns {Promise<Object>} `{report, bins, weight, warning}`, all optional.
 */
const crossValidateSamples = async (samples, { kinds, treeParams, train, t }) => {
  const requested = treeParams.cvScheme !== CV_SCHEMES.NONE && treeParams.cvFolds >= 2;
  const foldSizes = requested ? await eeEval(ee.FeatureCollection(samples).aggregate_histogram(FOLD_PROPERTY)) : {};
  const usableFolds = populatedFolds(foldSizes);

  if (usableFolds.length < 2) {
    if (!requested) return {};
    return {
      report: { CV_SCHEME: CV_SCHEMES.NONE, CV_DEGENERATE: treeParams.cvScheme },
      warning: t('forms.acquisition.5.bathymetry.treeCvDegenerate', { block: treeParams.cvBlockSizeM }),
    };
  }

  const outOfFold = crossValidate(samples, { kinds, folds: usableFolds, train });
  const weight =
    kinds.length > 1
      ? await eeEval(ensembleWeightFromOutOfFold(outOfFold, treeParams.ensembleWeighting))
      : treeParams.ensembleWeightRf;
  const scored = withPredictedDepth(outOfFold, kinds, weight);
  const measured = await eeEval(metricsOf(scored));

  return {
    weight,
    bins: await errorByDepthBin(withResiduals(scored)),
    report: {
      CV_SCHEME: treeParams.cvScheme,
      CV_FOLDS: usableFolds.length,
      ...(usableFolds.length !== treeParams.cvFolds && { CV_FOLDS_ASKED: treeParams.cvFolds }),
      ...(treeParams.cvScheme === CV_SCHEMES.BLOCK && { CV_BLOCK_M: treeParams.cvBlockSizeM }),
      CV_N: measured.n,
      CV_RMSE: measured.rmse,
      CV_MAE: measured.mae,
      CV_BIAS: measured.bias,
      CV_R2: measured.r2,
    },
  };
};

/**
 * @function runTreeModel
 * @description Fits a random forest, a gradient boosting or the ensemble of the two to the
 * soundings and applies it to the imagery - or applies a model loaded from a file instead, which
 * needs no soundings at all.
 * Nothing here touches the page: the refusals come back as messages and the fitted model comes
 * back whole, so the caller decides what to show and what to keep.
 * @param {Object} params
 * @param {Object} params.data - The analysis configuration.
 * @param {Object} params.fileData - The uploaded files' state.
 * @param {Object} params.region - The area of interest, as an `ee.Geometry`.
 * @param {Object} params.mission - The mission, for its bands and its optical resolution.
 * @param {String} params.type - The imagery's processing level, TOA or SR.
 * @param {Object} [params.reducer] - How several soundings in one pixel are combined.
 * @param {Function} params.t - The translator.
 * **Called by:
 * @see {ProcessingPage} - Bathymetry's result page, on the run.
 * @memberof module:Bathymetry
 * @returns {Promise<Object>} `{image, coefficients, trained, bins, warning}` on a fit, or
 *  `{error}` when the run cannot go ahead.
 */
export const runTreeModel = async ({ data, fileData, region, mission, type, reducer, t }) => {
  const treeParams = treeParamsOf(data.treeParams);
  const scale = mission.opticalResolution || 30;
  const context = {
    bands: data.bands,
    missionBands: mission.bands,
    predictorSet: treeParams.predictorSet,
    scale,
    crs: fileData.coordinatesSystem,
  };
  const predictors = predictorNames(context);

  if (data.loadedModel) {
    return applyLoadedModel({ loaded: data.loadedModel, data, type, context, predictors, params: treeParams, t });
  }
  if (!fileData?.matrix || !reducer) {
    return abort(t('forms.acquisition.5.bathymetry.treeNeedsTraining'), false);
  }

  const observedAll = observedDepthImage(fileData.matrix, reducer, region);
  const samples = stratifiedTrainingSamples({
    predictorImage: predictorImage(data.image, context),
    predictors,
    observedDepth: data?.validationSplit
      ? observedAll.updateMask(splitMasks({ scale, crs: fileData.coordinatesSystem }).train)
      : observedAll,
    region,
    scale,
    crs: fileData.coordinatesSystem,
    seed: treeParams.seed,
    depthLimit: data.depthLightExtinction,
    config: treeParams,
  });
  const sampleCount = await eeEval(ee.FeatureCollection(samples).size());
  if (!sampleCount) return abort(t('forms.acquisition.5.bathymetry.treeNoSamples'));

  const kinds = kindsFor(data.modelSelected);
  const validation = await crossValidateSamples(samples, {
    kinds,
    treeParams,
    t,
    train: (features, kind) =>
      classifierFor(kind, treeParams, predictors.length).train({
        features,
        classProperty: 'or_depth',
        inputProperties: predictors,
      }),
  });

  const calibration = { ...treeParams, ensembleWeightRf: validation.weight ?? treeParams.ensembleWeightRf };
  const classifiers = trainTreeModel(samples, { model: data.modelSelected, predictors, params: calibration });

  return {
    bins: validation.bins,
    warning: validation.warning,
    image: applyTreeModel(data.image, { ...context, params: calibration, classifiers }),
    trained: {
      model: data.modelSelected,
      predictors,
      params: { ...calibration, scale },
      classifiers,
      samples: sampleCount,
    },
    coefficients: {
      ...treeMetricsReport(data.modelSelected, predictors, calibration),
      TREE_N_SAMPLES: sampleCount,
      ...validation.report,
    },
  };
};
